import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  ArrowLeft, Building2, Users, GraduationCap, Plus, Upload,
  Loader2, RefreshCw, Download, CheckCircle2, XCircle, AlertCircle, UserPlus, FileText, BookCopy,
  Pencil, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { TopicAllocationTab } from "./TopicAllocationTab";

const API = process.env.REACT_APP_BACKEND_URL;
const ALL_GRADES   = ["G1","G2","G3","G4","G5","G6","G7","G8"];
const ALL_SUBJECTS = ["Mathematics","English","Hindi","EVS","Science","Social Science","Sanskrit"];

function authHdr(token) {
  return { Authorization:`Bearer ${token}`, "Content-Type":"application/json" };
}
function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN",{day:"2-digit",month:"short",year:"numeric"});
}

// ── CSV templates (data URI download) ────────────────────────────────────────
const STUDENT_CSV_TPL = `name,grade,pin,subject,teacher_id\nArjun Sharma,6,1111,Mathematics,\nPriya Singh,7,2222,Science,`;
const TEACHER_CSV_TPL = `name,email,password,grades,subjects\nMr. Kumar,kumar@school.edu,Teacher@123,"G6,G7","Mathematics,Science"\nMs. Sharma,sharma@school.edu,Pass@456,G8,"English,Hindi"`;

function downloadCSV(content, filename) {
  const blob = new Blob([content], { type:"text/csv" });
  const url  = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function buildCredentialSheet(students, school, sessionCreds) {
  const appUrl = window.location.origin + "/login";
  const header = [
    `# Credential Sheet — ${school.school_code} (${school.school_name})`,
    `# Generated: ${new Date().toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"})}`,
    `# PIN shown only for students added in the current session. "—" means PIN was set earlier — contact admin to reset.`,
    "",
    "Learner ID,Name,Grade,PIN,Assigned Teacher,App Login URL",
  ];
  const rows = students.map(s => {
    const pin = sessionCreds[s.learner_id] || "—";
    const teacher = s.teacher_id || "—";
    return `${s.learner_id},"${s.name}",${s.grade},${pin},${teacher},${appUrl}`;
  });
  return [...header, ...rows].join("\n");
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SchoolDetail({ token, onUnauth }) {
  const { schoolCode } = useParams();
  const [school,   setSchool]   = useState(null);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [tab,      setTab]      = useState("students");

  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showAddTeacher, setShowAddTeacher] = useState(false);
  const [csvResult,      setCsvResult]      = useState(null); // {created, errors, error_list}
  // Track plain PINs for students created this session (learner_id → pin)
  const [sessionCreds,   setSessionCreds]   = useState({});
  // Edit / delete student state
  const [editingStudent,  setEditingStudent]  = useState(null);
  const [deletingStudent, setDeletingStudent] = useState(null);
  const [deleteLoading,   setDeleteLoading]   = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, stuRes, tchRes] = await Promise.all([
        fetch(`${API}/api/admin/cr/schools/${schoolCode}`,          { headers: authHdr(token) }),
        fetch(`${API}/api/admin/cr/schools/${schoolCode}/students`, { headers: authHdr(token) }),
        fetch(`${API}/api/admin/cr/schools/${schoolCode}/teachers`, { headers: authHdr(token) }),
      ]);
      if (sRes.status === 401) { onUnauth(); return; }
      setSchool(await sRes.json());
      setStudents(await stuRes.json());
      setTeachers(await tchRes.json());
    } catch { toast.error("Failed to load school data"); }
    finally  { setLoading(false); }
  }, [schoolCode, token, onUnauth]);

  useEffect(() => { loadAll(); }, [loadAll]);

  if (loading || !school) return (
    <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-slate-400"/></div>
  );

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <Link to="/admin/schools" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-sky-700 mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4"/> Back to Schools
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-sky-600"/>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-slate-900">{school.school_name}</h1>
                {school.status === "active"
                  ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Active</Badge>
                  : <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Inactive</Badge>
                }
              </div>
              <div className="flex items-center gap-3 mt-1">
                <span className="font-mono text-sm font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded">{school.school_code}</span>
                <span className="text-slate-400 text-xs">·</span>
                <div className="flex gap-1">{[...(school.grade_range||[])].sort((a,b)=>parseInt(a.slice(1),10)-parseInt(b.slice(1),10)).map(g=><Badge key={g} variant="secondary" className="text-xs px-1.5 py-0 bg-violet-50 text-violet-700">{g}</Badge>)}</div>
                <span className="text-slate-400 text-xs">·</span>
                <div className="flex gap-1">{(school.subject_whitelist||[]).slice(0,3).map(s=><Badge key={s} variant="secondary" className="text-xs px-1.5 py-0 bg-emerald-50 text-emerald-700">{s}</Badge>)}{school.subject_whitelist?.length>3&&<span className="text-xs text-slate-400">+{school.subject_whitelist.length-3}</span>}</div>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="w-4 h-4 mr-1.5"/>Refresh</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-8">
        <Card className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Users className="w-5 h-5 text-blue-600"/></div>
          <div><p className="text-2xl font-bold text-slate-900">{students.length}</p><p className="text-sm text-slate-500">Students</p></div>
        </Card>
        <Card className="p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><GraduationCap className="w-5 h-5 text-amber-600"/></div>
          <div><p className="text-2xl font-bold text-slate-900">{teachers.length}</p><p className="text-sm text-slate-500">Teachers</p></div>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="students" data-testid="tab-students"><Users className="w-4 h-4 mr-1.5"/>Students ({students.length})</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers"><GraduationCap className="w-4 h-4 mr-1.5"/>Teachers ({teachers.length})</TabsTrigger>
          <TabsTrigger value="allocation" data-testid="tab-allocation"><BookCopy className="w-4 h-4 mr-1.5"/>Topic Allocation</TabsTrigger>
        </TabsList>

        {/* ── Students tab ── */}
        <TabsContent value="students">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">{students.length} student{students.length!==1?"s":""} registered</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={()=>downloadCSV(STUDENT_CSV_TPL,"student_template.csv")} data-testid="download-student-csv">
                <Download className="w-4 h-4 mr-1.5"/>CSV Template
              </Button>
              <CSVUploadButton
                label="Upload Students CSV"
                url={`${API}/api/admin/cr/schools/${schoolCode}/students/bulk`}
                token={token}
                onDone={(r)=>{
                  // Capture PINs from bulk upload response
                  const newCreds = {};
                  (r.records||[]).forEach(rec => { if (rec.pin) newCreds[rec.learner_id] = rec.pin; });
                  setSessionCreds(prev => ({...prev, ...newCreds}));
                  setCsvResult(r);
                  loadAll();
                }}
                testid="upload-students-csv"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={()=>downloadCSV(buildCredentialSheet(students, school, sessionCreds), `${schoolCode}_credentials_${new Date().toISOString().slice(0,10)}.csv`)}
                disabled={students.length === 0}
                data-testid="download-credential-sheet"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              >
                <FileText className="w-4 h-4 mr-1.5"/>Credential Sheet
              </Button>
              <Button size="sm" onClick={()=>setShowAddStudent(true)} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-student-btn">
                <UserPlus className="w-4 h-4 mr-1.5"/>Add Student
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-400 mb-3" data-testid="student-csv-format-hint">
            CSV format: <code className="bg-slate-100 px-1 rounded">name, grade, pin, subject, teacher_id</code> — column names are case-insensitive. <em>grade</em> can be omitted if the school has a single grade. <em>pin</em> defaults to 1111 if omitted. <em>subject</em> is optional — used for auto teacher mapping. <em>teacher_id</em> is auto-assigned by grade+subject if not provided.{" "}
            Example: <code className="bg-slate-100 px-1 rounded">Arjun Sharma, 6, 1111, Mathematics,</code>
          </p>
          <StudentTable
            students={students}
            teachers={teachers}
            token={token}
            schoolCode={schoolCode}
            onRefresh={loadAll}
            onEdit={(s) => setEditingStudent(s)}
            onDelete={(s) => setDeletingStudent(s)}
          />
        </TabsContent>

        {/* ── Teachers tab ── */}
        <TabsContent value="teachers">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500">{teachers.length} teacher{teachers.length!==1?"s":""} registered</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={()=>downloadCSV(TEACHER_CSV_TPL,"teacher_template.csv")} data-testid="download-teacher-csv">
                <Download className="w-4 h-4 mr-1.5"/>CSV Template
              </Button>
              <CSVUploadButton label="Upload Teachers CSV" url={`${API}/api/admin/cr/schools/${schoolCode}/teachers/bulk`} token={token} onDone={(r)=>{setCsvResult(r);loadAll();}} testid="upload-teachers-csv"/>
              <Button size="sm" onClick={()=>setShowAddTeacher(true)} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="add-teacher-btn">
                <GraduationCap className="w-4 h-4 mr-1.5"/>Add Teacher
              </Button>
            </div>
          </div>
          <TeacherTable teachers={teachers} token={token} schoolCode={schoolCode} onRefresh={loadAll}/>
        </TabsContent>

        {/* ── Topic Allocation tab ── */}
        <TabsContent value="allocation">
          <TopicAllocationTab school={school} token={token} />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      {showAddStudent && (
        <AddStudentModal
          token={token}
          schoolCode={schoolCode}
          teachers={teachers}
          onClose={()=>setShowAddStudent(false)}
          onCreated={(learner_id, pin)=>{
            setSessionCreds(prev => ({...prev, [learner_id]: pin}));
            setShowAddStudent(false);
            loadAll();
          }}
        />
      )}
      {showAddTeacher && (
        <AddTeacherModal token={token} schoolCode={schoolCode} school={school} onClose={()=>setShowAddTeacher(false)} onCreated={()=>{setShowAddTeacher(false);loadAll();}}/>
      )}

      {/* Edit student modal */}
      {editingStudent && (
        <EditStudentModal
          token={token}
          schoolCode={schoolCode}
          student={editingStudent}
          onClose={() => setEditingStudent(null)}
          onSaved={() => { setEditingStudent(null); loadAll(); }}
        />
      )}

      {/* Delete student confirm */}
      {deletingStudent && (
        <Dialog open={true} onOpenChange={() => setDeletingStudent(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5" /> Delete Student
              </DialogTitle>
            </DialogHeader>
            <div className="py-2 space-y-4">
              <p className="text-sm text-slate-600">
                Are you sure you want to delete <strong>{deletingStudent.name}</strong> ({deletingStudent.learner_id})?
                This will remove the student and all their progress data permanently.
              </p>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setDeletingStudent(null)} disabled={deleteLoading}>Cancel</Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white"
                  disabled={deleteLoading}
                  data-testid="confirm-delete-student-btn"
                  onClick={async () => {
                    setDeleteLoading(true);
                    try {
                      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/students/${deletingStudent.learner_id}`, {
                        method: "DELETE",
                        headers: authHdr(token),
                      });
                      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Delete failed"); return; }
                      toast.success(`Deleted ${deletingStudent.name}`);
                      setDeletingStudent(null);
                      loadAll();
                    } catch { toast.error("Network error"); }
                    finally { setDeleteLoading(false); }
                  }}
                >
                  {deleteLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</> : "Delete Student"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* CSV result toast-like panel */}
      {csvResult && (
        <CSVResultModal result={csvResult} onClose={()=>setCsvResult(null)}/>
      )}
    </div>
  );
}

// ── Student table ─────────────────────────────────────────────────────────────
function StudentTable({ students, teachers, token, schoolCode, onRefresh, onEdit, onDelete }) {
  const [gradeFilter, setGradeFilter] = useState("All");
  const [bulkMode,    setBulkMode]    = useState(false);
  const [selected,    setSelected]    = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  // Derive unique grades from the student list
  const uniqueGrades = [...new Set(students.map(s => `G${s.grade}`))].sort();

  const filtered = gradeFilter === "All" ? students : students.filter(s => `G${s.grade}` === gradeFilter);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(s => s.learner_id)));
  };

  const exitBulkMode = () => { setBulkMode(false); setSelected(new Set()); };

  const confirmBulkDelete = async () => {
    if (!selected.size) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/students/bulk-delete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ learner_ids: [...selected] }),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Bulk delete failed"); return; }
      const d = await res.json();
      toast.success(`${d.deleted} student${d.deleted !== 1 ? "s" : ""} deleted`);
      setShowBulkConfirm(false);
      exitBulkMode();
      onRefresh();
    } catch { toast.error("Network error"); }
    finally { setBulkDeleting(false); }
  };

  if (!students.length) return (
    <Card className="p-10 text-center border-dashed border-2 border-slate-200">
      <Users className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
      <p className="text-slate-500 text-sm">No students yet. Add one or upload a CSV.</p>
    </Card>
  );

  return (
    <>
      {/* Toolbar: grade filter + bulk delete controls */}
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        {/* Grade filter pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-slate-500 font-medium mr-1">Filter:</span>
          <button
            onClick={() => setGradeFilter("All")}
            data-testid="grade-filter-all"
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${gradeFilter === "All" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
          >All</button>
          {uniqueGrades.map(g => (
            <button
              key={g}
              onClick={() => setGradeFilter(g)}
              data-testid={`grade-filter-${g}`}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${gradeFilter === g ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}
            >{g}</button>
          ))}
          {gradeFilter !== "All" && (
            <span className="text-xs text-slate-400 ml-1">({filtered.length} shown)</span>
          )}
        </div>

        {/* Bulk delete controls */}
        {!bulkMode ? (
          <Button
            size="sm"
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 text-xs h-7"
            onClick={() => setBulkMode(true)}
            data-testid="bulk-delete-mode-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5"/>Delete Students
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{selected.size} selected</span>
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7"
              onClick={exitBulkMode}
              data-testid="bulk-cancel-btn"
            >Cancel</Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white text-xs h-7"
              disabled={!selected.size}
              onClick={() => setShowBulkConfirm(true)}
              data-testid="bulk-delete-confirm-trigger"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5"/>Delete ({selected.size})
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="students-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {bulkMode && (
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onCheckedChange={toggleAll}
                    data-testid="select-all-students"
                  />
                </th>
              )}
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Learner ID</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Name</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Grade</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Teacher(s)</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Created</th>
              {!bulkMode && <th className="text-left px-5 py-3 font-semibold text-slate-600">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map((s,i)=>(
              <tr
                key={s.learner_id}
                className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i%2===0?"":"bg-slate-50/40"} ${bulkMode && selected.has(s.learner_id) ? "bg-red-50/60" : ""}`}
                data-testid={`student-row-${s.learner_id}`}
              >
                {bulkMode && (
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selected.has(s.learner_id)}
                      onCheckedChange={() => toggleSelect(s.learner_id)}
                      data-testid={`select-student-${s.learner_id}`}
                    />
                  </td>
                )}
                <td className="px-5 py-3 font-mono text-xs text-sky-700 font-bold">{s.learner_id}</td>
                <td className="px-5 py-3 font-medium text-slate-800">{s.name}</td>
                <td className="px-5 py-3"><Badge variant="secondary" className="bg-violet-50 text-violet-700 text-xs">G{s.grade}</Badge></td>
                <td className="px-5 py-3">
                  {(() => {
                    const gradeTag = `G${s.grade}`;
                    const mapped = (teachers || []).filter(t => (t.grades || []).includes(gradeTag));
                    if (!mapped.length) return <span className="text-slate-300 text-xs">—</span>;
                    return (
                      <div className="space-y-0.5">
                        {mapped.map(t => (
                          <div key={t.teacher_id} className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-mono text-xs text-amber-700 font-semibold">{t.teacher_id}</span>
                            {(t.subjects || []).length > 0 && (
                              <span className="text-xs text-slate-400">({t.subjects.slice(0,2).join(', ')})</span>
                            )}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(s.created_at)}</td>
                {!bulkMode && (
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => onEdit(s)}
                        title="Edit student details"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-sky-200 text-sky-700 hover:bg-sky-50 transition-colors"
                        data-testid={`edit-student-${s.learner_id}`}
                      >
                        <Pencil size={12} /> Edit
                      </button>
                      <button
                        onClick={() => onDelete(s)}
                        title="Delete student"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-red-200 text-red-700 hover:bg-red-50 transition-colors"
                        data-testid={`delete-student-${s.learner_id}`}
                      >
                        <Trash2 size={12} /> Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bulk delete confirmation dialog */}
      {showBulkConfirm && (
        <Dialog open={true} onOpenChange={() => setShowBulkConfirm(false)}>
          <DialogContent className="max-w-sm" data-testid="bulk-delete-students-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5"/> Delete {selected.size} Students?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 mt-1">
              This will permanently delete <strong>{selected.size}</strong> student{selected.size !== 1 ? "s" : ""} and all their progress data. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowBulkConfirm(false)} disabled={bulkDeleting}>Cancel</Button>
              <Button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-bulk-delete-students-btn"
              >
                {bulkDeleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Deleting...</> : `Delete ${selected.size} Students`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Teacher table ─────────────────────────────────────────────────────────────
function TeacherTable({ teachers, token, schoolCode, onRefresh }) {
  const [editTarget,   setEditTarget]   = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [bulkMode,     setBulkMode]     = useState(false);
  const [selected,     setSelected]     = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showBulkConfirm, setShowBulkConfirm] = useState(false);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const toggleAll = () => {
    if (selected.size === teachers.length) setSelected(new Set());
    else setSelected(new Set(teachers.map(t => t.teacher_id)));
  };

  const exitBulkMode = () => { setBulkMode(false); setSelected(new Set()); };

  const confirmBulkDelete = async () => {
    if (!selected.size) return;
    setBulkDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/teachers/bulk-delete`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ teacher_ids: [...selected] }),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Bulk delete failed"); return; }
      const d = await res.json();
      toast.success(`${d.deleted} teacher${d.deleted !== 1 ? "s" : ""} deleted`);
      setShowBulkConfirm(false);
      exitBulkMode();
      onRefresh();
    } catch { toast.error("Network error"); }
    finally { setBulkDeleting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/teachers/${deleteTarget.teacher_id}`, {
        method: "DELETE", headers: authHdr(token),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Delete failed"); return; }
      toast.success(`Teacher ${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      onRefresh();
    } catch { toast.error("Network error"); }
    finally { setDeleting(false); }
  };

  if (!teachers.length) return (
    <Card className="p-10 text-center border-dashed border-2 border-slate-200">
      <GraduationCap className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
      <p className="text-slate-500 text-sm">No teachers yet. Add one or upload a CSV.</p>
    </Card>
  );
  return (
    <>
      {/* Bulk delete controls */}
      <div className="flex items-center justify-end mb-3">
        {!bulkMode ? (
          <Button
            size="sm"
            variant="outline"
            className="border-red-200 text-red-600 hover:bg-red-50 text-xs h-7"
            onClick={() => setBulkMode(true)}
            data-testid="bulk-delete-teachers-mode-btn"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1.5"/>Delete Teachers
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{selected.size} selected</span>
            <Button size="sm" variant="outline" className="text-xs h-7" onClick={exitBulkMode} data-testid="bulk-teachers-cancel-btn">Cancel</Button>
            <Button
              size="sm"
              className="bg-red-600 hover:bg-red-700 text-white text-xs h-7"
              disabled={!selected.size}
              onClick={() => setShowBulkConfirm(true)}
              data-testid="bulk-delete-teachers-confirm-trigger"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5"/>Delete ({selected.size})
            </Button>
          </div>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 overflow-x-auto bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="teachers-table">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              {bulkMode && (
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={teachers.length > 0 && selected.size === teachers.length}
                    onCheckedChange={toggleAll}
                    data-testid="select-all-teachers"
                  />
                </th>
              )}
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Teacher ID</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Name</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Email</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Grades</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Subjects</th>
              <th className="text-left px-5 py-3 font-semibold text-slate-600">Created</th>
              {!bulkMode && <th className="text-left px-5 py-3 font-semibold text-slate-600">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {teachers.map((t,i)=>(
              <tr
                key={t.teacher_id}
                className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${i%2===0?"":"bg-slate-50/40"} ${bulkMode && selected.has(t.teacher_id) ? "bg-red-50/60" : ""}`}
                data-testid={`teacher-row-${t.teacher_id}`}
              >
                {bulkMode && (
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={selected.has(t.teacher_id)}
                      onCheckedChange={() => toggleSelect(t.teacher_id)}
                      data-testid={`select-teacher-${t.teacher_id}`}
                    />
                  </td>
                )}
                <td className="px-5 py-3 font-mono text-xs text-amber-700 font-bold">{t.teacher_id}</td>
                <td className="px-5 py-3 font-medium text-slate-800">{t.name}</td>
                <td className="px-5 py-3 text-slate-500 text-xs">{t.email}</td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">{(t.grades||[]).map(g=><Badge key={g} variant="secondary" className="text-xs px-1.5 py-0 bg-violet-50 text-violet-700">{g}</Badge>)}</div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1">{(t.subjects||[]).map(s=><Badge key={s} variant="secondary" className="text-xs px-1.5 py-0 bg-emerald-50 text-emerald-700">{s}</Badge>)}</div>
                </td>
                <td className="px-5 py-3 text-slate-500 text-xs">{fmtDate(t.created_at)}</td>
                {!bulkMode && (
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-blue-600" onClick={()=>setEditTarget(t)} data-testid={`teacher-edit-${t.teacher_id}`}>
                        <Pencil className="w-3.5 h-3.5"/>
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-slate-500 hover:text-red-600" onClick={()=>setDeleteTarget(t)} data-testid={`teacher-delete-${t.teacher_id}`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editTarget && (
        <EditTeacherModal
          token={token}
          schoolCode={schoolCode}
          teacher={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); onRefresh(); }}
        />
      )}

      {deleteTarget && (
        <Dialog open={true} onOpenChange={() => setDeleteTarget(null)}>
          <DialogContent className="max-w-sm" data-testid="delete-teacher-confirm-dialog">
            <DialogHeader><DialogTitle>Delete Teacher?</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600 mt-1">
              Remove <strong>{deleteTarget.name}</strong> ({deleteTarget.teacher_id}) from this school? This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
              <Button onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-delete-teacher-btn">
                {deleting ? "Deleting…" : "Delete"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Bulk delete confirmation dialog */}
      {showBulkConfirm && (
        <Dialog open={true} onOpenChange={() => setShowBulkConfirm(false)}>
          <DialogContent className="max-w-sm" data-testid="bulk-delete-teachers-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <Trash2 className="w-5 h-5"/> Delete {selected.size} Teachers?
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 mt-1">
              This will permanently delete <strong>{selected.size}</strong> teacher{selected.size !== 1 ? "s" : ""} from this school. This cannot be undone.
            </p>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setShowBulkConfirm(false)} disabled={bulkDeleting}>Cancel</Button>
              <Button
                onClick={confirmBulkDelete}
                disabled={bulkDeleting}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-bulk-delete-teachers-btn"
              >
                {bulkDeleting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Deleting...</> : `Delete ${selected.size} Teachers`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Edit Teacher Modal ────────────────────────────────────────────────────────
function EditTeacherModal({ token, schoolCode, teacher, onClose, onSaved }) {
  const [name,     setName]     = useState(teacher.name || "");
  const [grades,   setGrades]   = useState(teacher.grades || []);
  const [subjects, setSubjects] = useState(teacher.subjects || []);
  const [newPw,    setNewPw]    = useState("");
  const [saving,   setSaving]   = useState(false);

  const toggleGrade   = g => setGrades(prev => prev.includes(g) ? prev.filter(x=>x!==g) : [...prev, g]);
  const toggleSubject = s => setSubjects(prev => prev.includes(s) ? prev.filter(x=>x!==s) : [...prev, s]);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!grades.length) { toast.error("Select at least one grade"); return; }
    if (!subjects.length) { toast.error("Select at least one subject"); return; }
    if (newPw && newPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), grades, subjects };
      if (newPw.trim()) body.new_password = newPw;
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/teachers/${teacher.teacher_id}`, {
        method: "PATCH",
        headers: { ...authHdr(token), "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Update failed"); return; }
      toast.success("Teacher updated");
      onSaved();
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-teacher-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-amber-600"/>Edit Teacher — {teacher.teacher_id}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Full Name</Label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" value={name} onChange={e=>setName(e.target.value)} data-testid="edit-teacher-name"/>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Grades</Label>
            <div className="flex flex-wrap gap-2">
              {ALL_GRADES.map(g=>(
                <button key={g} type="button" onClick={()=>toggleGrade(g)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${grades.includes(g)?"bg-violet-600 text-white border-violet-600":"bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}
                  data-testid={`edit-teacher-grade-${g}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Subjects</Label>
            <div className="grid grid-cols-2 gap-1.5">
              {ALL_SUBJECTS.map(s=>(
                <div key={s} className="flex items-center gap-2">
                  <Checkbox id={`ets-${s}`} checked={subjects.includes(s)} onCheckedChange={()=>toggleSubject(s)}/>
                  <Label htmlFor={`ets-${s}`} className="text-sm cursor-pointer font-normal">{s}</Label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">New Password <span className="text-slate-400 font-normal">(leave blank to keep existing)</span></Label>
            <input type="password" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Min. 6 characters" value={newPw} onChange={e=>setNewPw(e.target.value)} data-testid="edit-teacher-password"/>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="save-teacher-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Saving...</> : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit Student Modal ────────────────────────────────────────────────────────
function EditStudentModal({ token, schoolCode, student, onClose, onSaved }) {
  const [name, setName] = useState(student.name || "");
  const [newPin, setNewPin] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (newPin && !/^\d{4}$/.test(newPin)) { toast.error("PIN must be exactly 4 digits"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim() };
      if (newPin) body.new_pin = newPin;
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/students/${student.learner_id}`, {
        method: "PATCH",
        headers: authHdr(token),
        body: JSON.stringify(body),
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || "Update failed"); return; }
      toast.success("Student details updated");
      onSaved();
    } catch { toast.error("Network error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-student-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5 text-sky-600" /> Edit Student
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-xs text-slate-400 font-mono">ID: {student.learner_id}</p>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Full Name <span className="text-red-500">*</span></Label>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              value={name}
              onChange={e => setName(e.target.value)}
              data-testid="edit-student-name"
            />
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">New PIN <span className="text-slate-400 font-normal">(leave blank to keep current)</span></Label>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-500"
              maxLength={4}
              placeholder="4-digit PIN"
              value={newPin}
              onChange={e => setNewPin(e.target.value)}
              data-testid="edit-student-pin"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="edit-student-submit">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ── Add Student Modal ─────────────────────────────────────────────────────────
function AddStudentModal({ token, schoolCode, teachers, onClose, onCreated }) {
  const [form, setForm] = useState({ name:"", grade:"", pin:"1111", teacher_id:"" });
  const [saving, setSaving] = useState(false);

  // Filter teachers to only those who teach the selected grade
  const gradeTeachers = form.grade
    ? teachers.filter(t => (t.grades || []).includes(`G${form.grade}`))
    : teachers;

  // These are auto-assigned — same as gradeTeachers
  const autoTeachers = gradeTeachers;

  // Reset teacher selection when grade changes (if current teacher no longer matches)
  const handleGradeChange = (val) => {
    const newGradeTeachers = val ? teachers.filter(t => (t.grades || []).includes(`G${val}`)) : teachers;
    const stillValid = newGradeTeachers.some(t => t.teacher_id === form.teacher_id);
    setForm(f => ({ ...f, grade: val, teacher_id: stillValid ? f.teacher_id : "" }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!form.grade)       { toast.error("Grade required"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/students`,{
        method:"POST", headers:authHdr(token),
        body: JSON.stringify({ name:form.name.trim(), grade:parseInt(form.grade), pin:form.pin||"1111", teacher_id:form.teacher_id||null }),
      });
      if (!res.ok) { const e=await res.json(); toast.error(e.detail||"Failed to create student"); return; }
      const d = await res.json();
      toast.success(`Student created: ${d.learner_id}`);
      onCreated(d.learner_id, form.pin || "1111");
    } catch { toast.error("Network error"); }
    finally  { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="add-student-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-600"/>Add Student</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Full Name <span className="text-red-500">*</span></Label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="e.g. Arjun Sharma" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="student-name-input"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">Grade <span className="text-red-500">*</span></Label>
              <select className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" value={form.grade} onChange={e=>handleGradeChange(e.target.value)} data-testid="student-grade-select">
                <option value="">Select...</option>
                {[1,2,3,4,5,6,7,8].map(g=><option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">PIN (4 digits)</Label>
              <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" maxLength={4} placeholder="1111" value={form.pin} onChange={e=>setForm({...form,pin:e.target.value})} data-testid="student-pin-input"/>
            </div>
          </div>
          {/* Auto-assigned teachers for this grade — dynamic display */}
          {form.grade && (
            <div>
              <Label className="text-sm font-semibold mb-2 block">
                Auto-assigned Teachers
                <span className="ml-2 text-xs font-normal text-slate-400">(all teachers for Grade {form.grade})</span>
              </Label>
              {autoTeachers.length > 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                  {autoTeachers.map(t => (
                    <div key={t.teacher_id} className="flex items-center gap-2 flex-wrap">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0"/>
                      <span className="font-medium text-slate-700 text-sm">{t.name}</span>
                      <span className="font-mono text-xs text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded">{t.teacher_id}</span>
                      {(t.subjects || []).length > 0 && (
                        <span className="text-xs text-slate-500">— {t.subjects.join(', ')}</span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-500">
                  No teachers found for Grade {form.grade}. Add teachers first from the Teachers tab — they will be auto-assigned to all Grade {form.grade} students.
                </div>
              )}
            </div>
          )}
          {form.grade && autoTeachers.length > 0 && (
            <div>
              <Label className="text-sm font-semibold mb-1.5 block">
                Assign Class Teacher (optional)
                <span className="ml-2 text-xs font-normal text-slate-400">Extra/homeroom teacher assignment</span>
              </Label>
              <select
                className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                value={form.teacher_id}
                onChange={e=>setForm({...form,teacher_id:e.target.value})}
                data-testid="student-teacher-select"
              >
                <option value="">— none —</option>
                {gradeTeachers.map(t=>(
                  <option key={t.teacher_id} value={t.teacher_id}>
                    {t.name} ({t.teacher_id}){t.subjects?.length ? ` — ${t.subjects.join(", ")}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {form.grade && (
            <p className="text-slate-400 text-xs">Learner ID will be auto-generated: <span className="font-mono">{schoolCode}-G{form.grade}-###</span></p>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="student-submit-btn">
              {saving?<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating...</>:<><UserPlus className="w-4 h-4 mr-1.5"/>Create Student</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Add Teacher Modal ─────────────────────────────────────────────────────────
function AddTeacherModal({ token, schoolCode, school, onClose, onCreated }) {
  const [form, setForm] = useState({ name:"", email:"", password:"", grades:[], subjects:[] });
  const [saving, setSaving] = useState(false);

  const toggleGrade   = g => setForm(f=>({...f,grades:   f.grades.includes(g)   ?f.grades.filter(x=>x!==g)   :[...f.grades,g]}));
  const toggleSubject = s => setForm(f=>({...f,subjects: f.subjects.includes(s)?f.subjects.filter(x=>x!==s):[...f.subjects,s]}));

  const submit = async () => {
    if (!form.name.trim())     { toast.error("Name required"); return; }
    if (!form.email.trim())    { toast.error("Email required"); return; }
    if (!form.password.trim()) { toast.error("Password required"); return; }
    if (!form.grades.length)   { toast.error("Select at least one grade"); return; }
    if (!form.subjects.length) { toast.error("Select at least one subject"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/cr/schools/${schoolCode}/teachers`,{
        method:"POST", headers:authHdr(token),
        body: JSON.stringify({ name:form.name.trim(), email:form.email.trim().toLowerCase(), password:form.password, grades:form.grades, subjects:form.subjects }),
      });
      if (res.status===409) { const e=await res.json(); toast.error(e.detail||"Email already registered"); return; }
      if (!res.ok)          { const e=await res.json(); toast.error(e.detail||"Failed"); return; }
      const d = await res.json();
      toast.success(`Teacher created: ${d.teacher_id}`);
      onCreated();
    } catch { toast.error("Network error"); }
    finally  { setSaving(false); }
  };

  const allowedGrades   = ALL_GRADES;  // Teachers can teach any grade (not constrained by school grade_range)
  const allowedSubjects = school?.subject_whitelist || ALL_SUBJECTS;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="add-teacher-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><GraduationCap className="w-5 h-5 text-amber-600"/>Add Teacher</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Full Name <span className="text-red-500">*</span></Label>
            <input className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="e.g. Mr. Rahul Kumar" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} data-testid="teacher-name-input"/>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Email <span className="text-red-500">*</span></Label>
            <input type="email" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="teacher@school.edu" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} data-testid="teacher-email-input"/>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-1.5 block">Password <span className="text-red-500">*</span></Label>
            <input type="password" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500" placeholder="Min. 6 characters" value={form.password} onChange={e=>setForm({...form,password:e.target.value})} data-testid="teacher-password-input"/>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Grades <span className="text-red-500">*</span></Label>
            <div className="flex flex-wrap gap-2">
              {allowedGrades.map(g=>(
                <button key={g} type="button" onClick={()=>toggleGrade(g)} data-testid={`teacher-grade-${g}`} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${form.grades.includes(g)?"bg-violet-600 text-white border-violet-600":"bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>{g}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-semibold mb-2 block">Subjects <span className="text-red-500">*</span></Label>
            <div className="grid grid-cols-2 gap-1.5">
              {allowedSubjects.map(s=>(
                <div key={s} className="flex items-center gap-2">
                  <Checkbox id={`ts-${s}`} checked={form.subjects.includes(s)} onCheckedChange={()=>toggleSubject(s)} data-testid={`teacher-subject-${s}`}/>
                  <Label htmlFor={`ts-${s}`} className="text-sm cursor-pointer font-normal">{s}</Label>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="teacher-submit-btn">
              {saving?<><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Creating...</>:<><GraduationCap className="w-4 h-4 mr-1.5"/>Create Teacher</>}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── CSV Upload Button ──────────────────────────────────────────────────────────
function CSVUploadButton({ label, url, token, onDone, testid }) {
  const ref      = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(url, { method:"POST", headers:{ Authorization:`Bearer ${token}` }, body:fd });
      if (!res.ok) { const err=await res.json(); toast.error(err.detail||"Upload failed"); return; }
      const data = await res.json();
      onDone(data);
      toast.success(`${data.created} record${data.created!==1?"s":""} created${data.errors>0?`, ${data.errors} skipped`:""}`);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <>
      <input ref={ref} type="file" accept=".csv" onChange={handleFile} className="hidden" data-testid={`${testid}-input`}/>
      <Button variant="outline" size="sm" onClick={()=>ref.current?.click()} disabled={busy} data-testid={testid}>
        {busy?<Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>:<Upload className="w-4 h-4 mr-1.5"/>}
        {label}
      </Button>
    </>
  );
}

// ── CSV Result Modal ──────────────────────────────────────────────────────────
function CSVResultModal({ result, onClose }) {
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>CSV Upload Result</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="flex gap-4">
            <div className="flex items-center gap-2 text-emerald-600">
              <CheckCircle2 className="w-5 h-5"/><span className="text-lg font-bold">{result.created}</span>
              <span className="text-sm">created</span>
            </div>
            {result.errors > 0 && (
              <div className="flex items-center gap-2 text-red-500">
                <XCircle className="w-5 h-5"/><span className="text-lg font-bold">{result.errors}</span>
                <span className="text-sm">skipped</span>
              </div>
            )}
          </div>
          {result.error_list?.length > 0 && (
            <div className="border border-red-100 rounded-lg overflow-hidden">
              <div className="bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 flex items-center gap-1.5"><AlertCircle className="w-3.5 h-3.5"/>Rows with errors</div>
              <div className="max-h-40 overflow-y-auto divide-y divide-red-50">
                {result.error_list.map((e,i)=>(
                  <div key={i} className="px-3 py-1.5 text-xs text-red-600">Row {e.row}{e.name?` (${e.name})`:""}: {e.error}</div>
                ))}
              </div>
            </div>
          )}
          {result.records?.length > 0 && (
            <div className="border border-emerald-100 rounded-lg overflow-hidden">
              <div className="bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700">Created records</div>
              <div className="max-h-40 overflow-y-auto divide-y divide-emerald-50">
                {result.records.map((r,i)=>(
                  <div key={i} className="px-3 py-1.5 text-xs text-emerald-700 font-mono">{r.learner_id||r.teacher_id} — {r.name}</div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end pt-2 border-t">
            <Button onClick={onClose}>Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
